import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import classNames from 'classnames';

import { EventData, EventItem } from 'components/EventsCalendar/types';
import Link from 'components/Link';
import DateCalendar from 'components/DateCalendar';
import CustomDay from 'components/EventsCalendar/CustomDay';
import { useRouter } from 'next/router';
import Button from 'components/Button';
import styles from './styles.module.scss';

interface EventsCalendarProps {
  eventList: EventData;
  isLoading?: boolean;
  isAdmin?: boolean;
  organizationId?: string;
  onMonthChange: (startDate: string, endDate: string) => void;
}

function EventsCalendar({
  eventList,
  isLoading = false,
  organizationId,
  onMonthChange,
  isAdmin
}: EventsCalendarProps) {
  const { t, i18n } = useTranslation(['organizations']);
  const [highlightedDays, setHighlightedDays] = useState<number[]>([]);
  const [eventsByDay, setEventsByDay] = useState<{
    [key: number]: EventItem[];
  }>({});
  const [currentMonth, setCurrentMonth] = useState(dayjs().month());
  const [currentYear, setCurrentYear] = useState(dayjs().year());

  const router = useRouter();

  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  const handleMonthChange = (date: dayjs.Dayjs) => {
    const startOfMonth = date.startOf('month').toISOString();
    const endOfMonth = date.endOf('month').toISOString();
    onMonthChange(startOfMonth, endOfMonth);
    setCurrentMonth(date.month()); // it starts at month 0
    setCurrentYear(date.year());
  };

  useEffect(() => {
    const days: number[] = [];
    const events: { [key: number]: EventItem[] } = {};
    eventList?.items.forEach((event: EventItem) => {
      const date = dayjs(event.startDate);
      if (date.month() === currentMonth) {
        const day = date.date();
        if (!days.includes(day)) {
          days.push(day);
        }
        if (!events[day]) {
          events[day] = [];
        }
        events[day].push(event);
      }
    });
    setHighlightedDays(days);
    setEventsByDay(events);
  }, [eventList?.items, currentMonth]);

  const slots = useMemo(
    () => ({
      day: (props) => (
        <CustomDay
          day={props.day}
          outsideCurrentMonth={props.outsideCurrentMonth}
          highlightedDays={highlightedDays}
          eventsByDay={eventsByDay}
          onDaySelect={props.onDaySelect}
          isFirstVisibleCell={props.isFirstVisibleCell}
          isLastVisibleCell={props.isLastVisibleCell}
          currentMonth={currentMonth}
          currentYear={currentYear}
          isAdmin={isAdmin}
        />
      )
    }),
    [highlightedDays, eventsByDay]
  );

  const minDate = dayjs().subtract(3, 'year');
  const maxDate = dayjs().add(3, 'year');

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language}
      key={i18n.language}
    >
      {organizationId && (
        <div className="flex flex-col sm:flex-row sm:justify-between mb-4 gap-3 sm:gap-0 sm:items-center">
          <Link href={`/organizations/${organizationId}/events`}>
            {t('calendar.events')}
            <img
              src="/img/icons/arrow-outward.svg"
              alt="Events link"
              height={16}
              width={16}
              className="inline ml-1"
            />
          </Link>
          {isAdmin && (
            <Button
              variant="secondary"
              onClick={() =>
                router.push(`/events/create?orgId=${organizationId}`)
              }
            >
              {t('calendar.create')}
            </Button>
          )}
        </div>
      )}
      <div className="w-full max-w-full overflow-hidden">
        <div className={classNames('w-full max-w-full', styles.calendar)}>
          <DateCalendar
            dayOfWeekFormatter={(day) => day.format('ddd')}
            renderLoading={() => <DayCalendarSkeleton />}
            loading={isLoading}
            onMonthChange={handleMonthChange}
            slots={slots}
            views={['year', 'month', 'day']}
            minDate={minDate}
            maxDate={maxDate}
          />
        </div>
      </div>
    </LocalizationProvider>
  );
}

export default EventsCalendar;
